import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from '../atoms/button';
import { Input } from '../atoms/input';
import { Label } from '../atoms/label';
import { Badge } from '../atoms/badge';
import Icon from '../molecules/Icon';
import RichTextEditor, { RichTextEditorRef } from '../molecules/RichTextEditor';
import { showToast } from '../molecules/toast-helpers';
import { LoaderCircle, CircleCheck } from 'lucide-react';

export interface StartupInfoFormData {
  name: string;
  project_type: string;
  language: string;
  industry: string;
  target_icp: string;
  problem_details: string;
}

export interface StartupInfoSubmit extends StartupInfoFormData {}

export interface StartupInfoFormProps {
  initialValues?: Partial<StartupInfoFormData>;
  isUpdating?: boolean;
  isWeb?: boolean;
  mode?: 'create' | 'update';
  onSubmit: (data: StartupInfoSubmit) => Promise<void>;
  onCancel?: () => void;
  /** When true, hides the internal action row — caller provides its own (e.g. drawer footer). */
  hideActions?: boolean;
  className?: string;
}

const PROJECT_TYPES = [
  { label: 'Web App', value: 'web_app' },
  { label: 'Website', value: 'website' },
  { label: 'Mobile App', value: 'mobile_app' },
];

const LANGUAGES = [
  { label: 'English', value: 'english' },
  { label: 'Arabic', value: 'arabic' },
  { label: 'Bilingual', value: 'bilingual' },
];

const FORM_ID = 'startup-info-form';

export const StartupInfoForm: React.FC<StartupInfoFormProps> = ({
  initialValues = {},
  isUpdating = false,
  isWeb = false,
  mode = 'update',
  onSubmit,
  onCancel,
  hideActions = false,
  className,
}) => {
  const isCreate = mode === 'create';
  const [formData, setFormData] = useState<StartupInfoFormData>({
    name: '',
    project_type: '',
    language: '',
    industry: '',
    target_icp: '',
    problem_details: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const originalRef = useRef<StartupInfoFormData | null>(null);
  const editorRef = useRef<RichTextEditorRef>(null);

  // Reset form whenever the initialValues identity changes (e.g. switching startups).
  // Callers MUST memoize `initialValues` — passing a fresh object literal on every
  // render will fire this effect on each keystroke and wipe user input.
  useEffect(() => {
    const initialized: StartupInfoFormData = {
      name: initialValues.name ?? '',
      project_type: initialValues.project_type ?? '',
      language: initialValues.language ?? '',
      industry: initialValues.industry ?? '',
      target_icp: initialValues.target_icp ?? '',
      problem_details: initialValues.problem_details ?? '',
    };
    setFormData(initialized);
    originalRef.current = initialized;
    // Don't imperatively setContent — Quill takes `formData.problem_details`
    // via the controlled `initialValue` prop below. The two paths (mutating
    // innerHTML vs. React's value prop) race and can leave Quill's Delta
    // out of sync with the DOM.
  }, [initialValues]);

  const hasChanges = useMemo(() => {
    if (!originalRef.current) return false;
    return (Object.keys(formData) as (keyof StartupInfoFormData)[]).some(
      (k) => formData[k] !== originalRef.current![k]
    );
  }, [formData]);

  const handleChange = (field: keyof StartupInfoFormData, value: string) => {
    setFormData((prev) => {
      if ((field === 'project_type' || field === 'language') && prev[field] === value) {
        return { ...prev, [field]: '' };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleProblemDetailsChange = (content: string) => {
    setFormData((prev) => ({ ...prev, problem_details: content }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name?.trim() || isLoading || isUpdating) return;
    setIsLoading(true);
    try {
      const rawProblem = editorRef.current?.getContent() ?? formData.problem_details;
      const isEmptyRichText = (html: string) =>
        !html || !html.replace(/<[^>]+>/g, '').replace(/&nbsp;|\s+/g, '').length;
      const data: StartupInfoSubmit = {
        ...formData,
        problem_details: isEmptyRichText(rawProblem) ? '' : rawProblem,
      };
      await onSubmit(data);
      originalRef.current = { ...data };
      showToast({
        message: isCreate ? 'Startup created successfully' : 'Startup info updated successfully',
        severity: 'success',
      });
    } catch (err) {
      console.error(isCreate ? 'Failed to create startup:' : 'Failed to update startup info:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const isBusy = isLoading || isUpdating;
  const submitDisabled = isBusy || !formData.name?.trim() || (!isCreate && !hasChanges);

  return (
    <div className={className}>
      <form id={FORM_ID} onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="startup-name">Startup name</Label>
          <Input
            id="startup-name"
            value={formData.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange('name', e.target.value)
            }
            placeholder="Startup name"
          />
        </div>

        <div className="space-y-2">
          <Label>Project type</Label>
          <div className="flex gap-2 flex-wrap">
            {PROJECT_TYPES.map((opt) => (
              <Badge
                key={opt.value}
                className="cursor-pointer"
                size="xs"
                variant={formData.project_type === opt.value ? 'success' : 'secondary'}
                onClick={() => handleChange('project_type', opt.value)}
                leftIcon={
                  formData.project_type === opt.value && (
                    <Icon icon={CircleCheck} size={14} className="stroke-current" />
                  )
                }
              >
                {opt.label}
              </Badge>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label>Language</Label>
          <div className="flex gap-2 flex-wrap">
            {LANGUAGES.map((opt) => (
              <Badge
                key={opt.value}
                className="cursor-pointer"
                size="xs"
                variant={formData.language === opt.value ? 'success' : 'secondary'}
                onClick={() => handleChange('language', opt.value)}
                leftIcon={
                  formData.language === opt.value && (
                    <Icon icon={CircleCheck} size={14} className="stroke-current" />
                  )
                }
              >
                {opt.label}
              </Badge>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="industry">Industry</Label>
          <Input
            id="industry"
            value={formData.industry}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange('industry', e.target.value)
            }
            placeholder="e.g. E-commerce, Healthcare, Fintech"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="target-icp">Target ICP</Label>
          <Input
            id="target-icp"
            value={formData.target_icp}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange('target_icp', e.target.value)
            }
            placeholder="e.g. SMB founders in MENA region aged 25-40"
          />
        </div>

        <div className="space-y-2">
          <Label>Problem you are solving</Label>
          <div className="min-h-[120px]">
            <RichTextEditor
              ref={editorRef}
              initialValue={formData.problem_details}
              placeholder="Describe the problem your startup solves in detail"
              onChange={handleProblemDetailsChange}
            />
          </div>
        </div>
      </form>

      {!hideActions && (
        <div className="flex items-center justify-end gap-3 pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
          {onCancel && (
            <Button variant="secondary-gray" size={isWeb ? 'sm' : 'xs'} onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button
            variant="primary"
            size={isWeb ? 'sm' : 'xs'}
            type="submit"
            form={FORM_ID}
            disabled={submitDisabled}
          >
            {isBusy && (
              <Icon
                icon={LoaderCircle}
                size={18}
                strokeWidth={2}
                color="#fff"
                className="animate-spin mr-2"
              />
            )}
            {isBusy ? (isCreate ? 'Creating...' : 'Updating...') : isCreate ? 'Create' : 'Update'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default StartupInfoForm;
